import { Link, useParams } from 'react-router-dom';
import { Badge, ErrorBanner, PageHeader, PageLoading } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { repositoryApi } from '@/services/repository-api';
import { orgHierarchyApi } from '@/services/org-hierarchy-api';
import { schemaRelationshipApi } from '@/services/schema-relationship-api';
import { issueCurrentApi } from '@/services/issue-current-api';
import { incidentApi } from '@/services/incident-api';
import { portfolioApi } from '@/services/portfolio-api';
import { PageContainer } from '@/components/layout';

function statusVariant(status: string): 'default' | 'warning' | 'secondary' {
  if (status === 'complete') {
    return 'default';
  }
  if (status === 'partial') {
    return 'warning';
  }
  return 'secondary';
}

export function RepositoryDetailsPage() {
  const { id } = useParams<{ id: string }>();

  const { data: repository, loading: repositoryLoading, error: repositoryError } = useApi(
    async () => {
      if (!id) {
        return null;
      }
      return await repositoryApi.getById(id);
    },
    [id],
  );

  const { data: hierarchy, error: hierarchyError } = useApi(
    () => orgHierarchyApi.getAll(false),
    [],
  );

  const { data: snapshot, error: snapshotError } = useApi(
    () => schemaRelationshipApi.getLatest(),
    [],
  );

  const { data: issueSummary, error: issueError } = useApi(
    async () => {
      if (!id) {
        return null;
      }

      const currentRepository = await repositoryApi.getById(id);
      const firstProduct = currentRepository?.products[0];
      if (!firstProduct) {
        return null;
      }

      return await issueCurrentApi.getSummary({ productId: firstProduct.id });
    },
    [id],
  );

  // PRODOPSAI-415: replace the mislabelled "Open Issues" (which is actually
  // total non-archived issues including Done) with the canonical
  // Work-In-Progress count from flowMetricsRepository. The other four
  // badges in the same row still use issueSummary / incidentSummary —
  // their semantics are unaffected by this story.
  const { data: wipCount, error: wipError } = useApi(
    async () => {
      if (!id) {
        return null;
      }

      const currentRepository = await repositoryApi.getById(id);
      const firstProduct = currentRepository?.products[0];
      if (!firstProduct) {
        return null;
      }

      const result = await portfolioApi.getCurrentWipCount({ productId: firstProduct.id });
      return result.value;
    },
    [id],
  );

  const { data: incidentSummary, error: incidentError } = useApi(
    async () => {
      if (!id) {
        return null;
      }

      const currentRepository = await repositoryApi.getById(id);
      const firstProduct = currentRepository?.products[0];
      if (!firstProduct) {
        return null;
      }

      return await incidentApi.getSummary({ productId: firstProduct.id });
    },
    [id],
  );

  if (repositoryLoading && !repository) {
    return <PageLoading />;
  }

  if (!id || !repository) {
    return (
      <div className="space-y-6">
        <PageHeader title="Repository Details" subtitle="Repository not found." backTo="/admin/repository-connections" />
        <ErrorBanner message={repositoryError ?? 'Repository not found.'} />
      </div>
    );
  }

  const relatedTeams = repository.products.flatMap((prod) => {
    const match = hierarchy?.products.find((candidate) => candidate.id === prod.id);
    return match?.teams ?? [];
  });

  const pathByName = new Map<string, string>();
  for (const path of snapshot?.graph.paths ?? []) {
    pathByName.set(path.name, path.status);
  }

  const orgStatus = pathByName.get('Org Hierarchy') ?? 'unknown';
  const deliveryStatus = pathByName.get('Delivery Chain') ?? 'unknown';
  const unifiedStatus = pathByName.get('End-to-End Delivery Ownership') ?? 'unknown';
  const repositoryEntityPresent = (snapshot?.graph.nodes ?? []).some((node) => node.id === 'public.repositories');

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader
        title={repository.name}
        subtitle="Repository details, ownership links, and delivery path coverage."
        backTo="/admin/repository-connections"
      />

      <ErrorBanner message={hierarchyError ?? snapshotError ?? issueError ?? incidentError ?? wipError} />

      <div className="rounded-md border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-medium">Repository</h3>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge variant={repository.fabricSynced ? 'success' : 'secondary'}>
            {repository.fabricSynced ? 'Fabric Synced' : 'Manual'}
          </Badge>
          <Badge variant={repository.archived ? 'warning' : 'success'}>
            {repository.archived ? 'Archived' : 'Active'}
          </Badge>
          <span className="text-muted-foreground">Products:</span>
          <span className={repository.products.length > 0 ? '' : 'text-muted-foreground'}>
            {repository.products.length > 0 ? repository.products.map((p) => p.name).join(', ') : 'Unassigned'}
          </span>
          <span className="text-muted-foreground">Fabric Repository ID:</span>
          <code>{repository.fabricRepositoryId ?? '-'}</code>
        </div>
      </div>

      <div className="rounded-md border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-medium">Team Connections</h3>
        {relatedTeams.length === 0 && (
          <p className="text-sm text-muted-foreground">No teams connected through the assigned product.</p>
        )}
        {relatedTeams.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {relatedTeams.map((team) => (
              <Link
                key={team.id}
                to={`/admin/teams/${team.id}`}
                className="inline-flex items-center rounded-md border border-border px-2 py-1 text-sm text-primary hover:underline"
              >
                {team.name}
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-md border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-medium">Delivery & Relationship Coverage</h3>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge variant={statusVariant(orgStatus)}>Org Path: {orgStatus}</Badge>
          <Badge variant={statusVariant(deliveryStatus)}>Delivery Path: {deliveryStatus}</Badge>
          <Badge variant={statusVariant(unifiedStatus)}>Unified Path: {unifiedStatus}</Badge>
          <Badge variant={repositoryEntityPresent ? 'success' : 'secondary'}>
            Schema Node: {repositoryEntityPresent ? 'public.repositories present' : 'missing'}
          </Badge>
          <Link to="/admin/schema-relationships" className="text-primary hover:underline font-medium">
            Open Full Schema Relationships
          </Link>
        </div>
      </div>

      <div className="rounded-md border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-medium">Product-level Signal</h3>
        <div className="flex flex-wrap gap-3 text-sm">
          <Badge variant="secondary">Work In Progress: {wipCount ?? 0}</Badge>
          <Badge variant="secondary">Assigned Issues: {issueSummary?.assigned ?? 0}</Badge>
          <Badge variant="secondary">Unassigned Issues: {issueSummary?.unassigned ?? 0}</Badge>
          <Badge variant="secondary">Incidents: {incidentSummary?.total ?? 0}</Badge>
          <Badge variant="secondary">Assigned Incidents: {incidentSummary?.assigned ?? 0}</Badge>
        </div>
      </div>
    </PageContainer>
  );
}
